import { escapeHTML } from '../../utils/escape.js';
import { formatPrice, formatFechaHora, formatMonto } from '../../utils/formatters.js';
import { estadoPago } from '../../shared/liquidacion-pagos-policy.js';

/**
 * Vista de Liquidación del panel de staff (Control de Horarios y
 * Liquidación de Sueldos, slice 5) — mount legacy `#admin-liquidacion-mount`.
 * Función de render pura: `js/admin.js` (wiring) le pasa `{ empleados }`
 * (de `admin-empleados.service.js#loadEmpleados`) + un ui-state con el
 * resultado del confirm, el historial y la configuración. El diálogo de
 * confirmación vive en su PROPIO mount (mismo patrón que el borrado de
 * bloqueos) — este componente no lo renderiza.
 *
 * S8 (design.md "File Changes", spec `payroll-period-dashboard`: "the
 * dashboard is the sole settle path" — ver apply-progress.md "Unit S8"):
 * `renderRangoForm` (selector mes/empleados) y `renderPreviewSection` (el
 * preview por lote, con el gate RF-11 de anomalías y el bloqueo de Fudo) se
 * RETIRARON — el tablero de período (`LiquidacionTableroView.js`, S2-S7) es
 * el ÚNICO camino para liquidar desde S8. Lo que sigue viviendo acá porque
 * el tablero lo REUSA VERBATIM (design.md AD1, "Technical Approach"):
 *   - El resultado del confirm (`renderConfirmResultSection`): un `kind:
 *     'rechazo_parcial'` lista EXACTAMENTE qué quedó escrito y qué no; un
 *     `kind: 'tope_no_configurado'`/`'precondicion_bloqueante'` se muestran
 *     con su propio bloqueo, nunca el error genérico. `iniciarLiquidarDesdeTablero`
 *     (`js/admin.js`, S7) repinta este panel como efecto secundario aceptado
 *     y documentado (apply-progress.md "Unit S7") cuando liquida desde el
 *     tablero.
 *   - El editor del tope mensual (E1) y el panel de consumo por mes, dentro
 *     del área de Configuración (gear button).
 *   - El historial de liquidaciones confirmadas (E2), ahora con la pill de
 *     estado de pago + "Registrar pago" (S8, spec `payroll-payments`).
 *
 * D2 correction, follow-ups C2/C3 (2026-08-09): `kind: 'tope_no_configurado'`
 * se muestra como bloqueo de configuración, nunca como el error genérico con
 * Reintentar. `kind: 'precondicion_bloqueante'` (409 `periodo_solapado`/
 * `liquidacion_previa_sin_descuento`, un gate del BATCH COMPLETO que corre
 * antes de cualquier persistencia) nombra a los empleados/liquidaciones
 * involucrados — nunca reusa `rechazo_parcial`, que implicaría un guardado
 * parcial que acá no puede haber ocurrido.
 *
 * E1 (employee-payroll-extensions, editor de tope): esta vista además
 * renderiza el editor del tope mensual de descuento (`uiState.capConfig`,
 * cargado/guardado por `js/admin.js` vía `admin-payroll-config.service.js`).
 *
 * E2 (historial + panel del mes): al pie de la vista, agrega el historial
 * de liquidaciones confirmadas y el consumo del tope por mes
 * (`uiState.historial`, cargado por `js/admin.js` vía
 * `admin-liquidacion.service.js#loadLiquidacionesHistorial` →
 * `liquidaciones.repository.js#listLiquidaciones`). AD9: es de PANTALLA,
 * NUNCA autoritativo para el cálculo de sueldos — una falla ahí SOLO
 * degrada ese bloque, nunca toca el resultado del confirm.
 */

const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';
const SUBMIT_BTN =
    'px-8 py-3 bg-brand text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none';
const BLOCKING_BOX = 'bg-red-50 border border-red-300 text-red-900 rounded-2xl p-6 mb-6';
const WARNING_BOX = 'bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl p-6 mb-6';
// S8 — spec `payroll-payments` "Derived Payment State" (misma tabla de
// tonos que `LiquidacionTableroView.js`'s `PAGO_PILL`: pendiente gris,
// parcial amarillo, pagado verde). No se importa de ahí para no acoplar dos
// vistas por un detalle de estilo — mismo criterio que el resto de este
// archivo (cada vista es dueña de sus propias clases Tailwind).
const PAGO_PILL = {
    pendiente: 'inline-block ml-2 px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-bold whitespace-nowrap',
    parcial: 'inline-block ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold whitespace-nowrap',
    pagado: 'inline-block ml-2 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-bold whitespace-nowrap',
};

/**
 * D2 correction, follow-up C3: `tope_no_configurado` (503) es un problema
 * de configuración, no una falla transitoria — mismo tratamiento visual
 * que `renderFudoBloqueado` (SIN botón Reintentar: reintentar solo no
 * arregla nada hasta que un admin cargue el tope).
 * @param {string} mensaje
 * @returns {string}
 */
function renderTopeNoConfigurado(mensaje) {
    return `
        <div role="alert" class="${BLOCKING_BOX}">
            <p class="font-bold mb-2">No se puede liquidar todavía.</p>
            <p class="text-sm">${escapeHTML(mensaje)}</p>
        </div>
    `;
}

/**
 * D2 correction, follow-up C2: `periodo_solapado`/`liquidacion_previa_sin_descuento`
 * (fases 1c/1d) bloquean el BATCH COMPLETO antes de la fase de
 * persistencia — nada se escribió, para nadie. Nunca reusar
 * `renderRechazoParcial` acá: ese render asume que algo pudo haber
 * quedado escrito, y acá eso es imposible por construcción. Lista a los
 * empleados/liquidaciones involucrados por nombre, nunca "ese empleado".
 * @param {{ mensaje: string, detalle?: Array<{ empleado_id: string, liquidacion_id: string, periodo_desde?: string, periodo_hasta?: string }> }} result
 * @param {Array<object>} empleados
 * @returns {string}
 */
function renderPrecondicionBloqueante(result, empleados) {
    const nombrePorId = new Map((empleados ?? []).map((e) => [e.id, e.nombre]));
    const detalle = result.detalle ?? [];
    const items = detalle.length
        ? `<ul class="list-disc pl-5 text-sm space-y-1 my-2">${detalle
              .map((d) => {
                  const periodo = d.periodo_desde && d.periodo_hasta ? ` (${escapeHTML(d.periodo_desde)} a ${escapeHTML(d.periodo_hasta)})` : '';
                  return `<li>${escapeHTML(nombrePorId.get(d.empleado_id) ?? 'Empleado')} — liquidación #${escapeHTML(d.liquidacion_id ?? '')}${periodo}</li>`;
              })
              .join('')}</ul>`
        : '';

    return `
        <div role="alert" class="${BLOCKING_BOX}">
            <p class="font-bold mb-2">No se guardó nada — hay que resolver esto antes de liquidar.</p>
            <p class="text-sm">${escapeHTML(result.mensaje)}</p>
            ${items}
        </div>
    `;
}

/**
 * E1 — editor del tope mensual de descuento (`payroll_config`, AD11).
 * `configurado: false` cubre TANTO "sin fila todavía" (AD11) COMO
 * "Supabase no disponible" — el servicio (`admin-payroll-config.service.js
 * #loadPayrollConfig`) documenta por qué colapsa ambos casos a propósito;
 * esta vista respeta esa misma honestidad y nunca pinta un tope en $0
 * cuando en realidad no hay ninguno confiable para mostrar.
 *
 * F3 (review-050ef9b16341fee9): el editor SIEMPRE muestra una advertencia de
 * que cambiar el tope es retroactivo dentro del mes en curso — `/api/liquidacion`
 * lee el tope VIGENTE en cada request y lo compara contra consumo ya
 * congelado de ese mes ART (ver `design.md` AD9/AD10), así que bajarlo a
 * mitad de mes genera excedentes de inmediato para liquidaciones todavía no
 * confirmadas. No bloquea el cambio, solo lo hace visible.
 *
 * F4 (mismo lineage): cuando hay `updatedAt`, muestra cuándo fue el último
 * cambio (mismo formato que el resto del panel admin). `updatedBy` es un
 * uuid de `profiles` que esta vista NO resuelve a nombre (fuera de este
 * scope) — nunca se imprime el uuid crudo; en su lugar se dice honestamente
 * que el panel no puede mostrar el nombre.
 *
 * Loose end F4 (review-13e17d2352297ac9's own follow-up, closed): el
 * call-site ahora envuelve `renderCapEditor` en `renderInformativePanel`
 * igual que el panel de historial. Fue registrado dos veces como "fuera de
 * alcance" con el argumento de que este es un formulario activo, no un
 * panel puramente informativo — pero `renderInformativePanel` no distingue
 * entre ambos: sólo decide si una excepción tira abajo el `<section>`
 * entero (perdiendo confirm/preview con cifras YA calculadas) o degrada a
 * un mensaje corto. Envolverlo es estrictamente más seguro: una
 * liquidación que ya tiene sus montos no puede depender de que el editor
 * de tope renderice bien. El costo es que, mientras el editor esté
 * degradado, no se puede cambiar el tope desde este panel — un problema
 * real pero estrictamente menos grave que bloquear una confirmación.
 * `bindLiquidacionMountListeners` (`js/admin.js`) ya guardaba con
 * `if (capForm)` antes de este cambio, así que el binding del submit no
 * revienta cuando el form no está en el DOM.
 * Unit S6 (design.md, spec `payroll-holidays-config`): exported so
 * `ConfiguracionLiquidacionView.js` can reuse this EXACT markup/shape inside
 * the tablero's "Configuración" drawer instead of duplicating it — the
 * requirement is literally "hosted unchanged", and the wiring
 * (`refreshLiquidacionCapConfig`/`handleLiquidacionCapSaveSubmit` in
 * `js/admin.js`) is reused too, not re-derived.
 * @param {{ cargando?: boolean, configurado?: boolean, tope?: number|null, saving?: boolean, error?: string|null, draft?: string|null, updatedAt?: string|null, updatedBy?: string|null }|null} capConfig
 * @returns {string}
 */
export function renderCapEditor(capConfig) {
    const cargando = capConfig?.cargando ?? false;
    const configurado = capConfig?.configurado ?? false;
    const saving = capConfig?.saving ?? false;
    const error = capConfig?.error ?? null;
    const draft = capConfig?.draft ?? '';

    const estadoHtml = cargando
        ? '<p class="text-sm text-text-muted mb-3">Cargando tope vigente...</p>'
        : configurado
          ? `<p class="text-sm text-text-muted mb-3">Tope vigente: <span class="font-bold text-text-primary">${formatPrice(capConfig?.tope ?? null)}</span></p>`
          : '<p class="text-sm text-text-muted mb-3">Todavía no hay un tope configurado — no es posible liquidar sueldos hasta que se cargue uno.</p>';

    // S8 (acceptance run): `payroll_config.updated_at/updated_by` also move
    // when "Período por defecto" is saved (same row), so this is the last
    // change of the CONFIGURATION, not of the tope. Kept (audit), relabeled.
    const ultimoCambioHtml = capConfig?.updatedAt
        ? `<p class="text-xs text-text-muted mb-3">Último cambio de la configuración: ${escapeHTML(formatFechaHora(capConfig.updatedAt))}${
              capConfig?.updatedBy ? ' · autor no identificable por nombre en este panel.' : '.'
          }</p>`
        : '';

    const retroactividadHtml =
        '<p class="text-xs text-amber-800 mb-3">Cambiar este tope afecta de inmediato las liquidaciones todavía no confirmadas del mes en curso, incluidos los períodos que caen en un mes ya parcialmente liquidado.</p>';

    const errorHtml = error ? `<p role="alert" class="text-xs font-bold text-red-700 mb-3">${escapeHTML(error)}</p>` : '';

    return `
        <div class="bg-surface border border-border-subtle rounded-2xl p-6 mb-8">
            <h3 class="${LABEL} mb-3">Tope mensual de descuento</h3>
            ${estadoHtml}
            ${ultimoCambioHtml}
            ${retroactividadHtml}
            ${errorHtml}
            <form data-action-form="payroll-config-tope" class="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                <div class="flex-1">
                    <label class="${LABEL}">Nuevo tope ($)</label>
                    <input type="number" min="0" step="1" name="tope_descuento_mensual" value="${escapeHTML(draft)}" class="${FIELD}" />
                </div>
                <button type="submit" data-action="payroll-config-guardar" ${saving ? 'disabled' : ''} class="${SUBMIT_BTN}">
                    Guardar tope
                </button>
            </form>
        </div>
    `;
}

/**
 * E2 — agrupa el consumo del tope por empleado+mes desde el `por_mes` YA
 * CONGELADO de cada fila del historial (`listLiquidaciones`, ordenado por
 * `creada_at` descendente) — nunca recalcula nada, sólo suma
 * `descuento_previo + descuento_imputado` de la fila MÁS RECIENTE que tiene
 * esa combinación (por eso itera en el orden que ya trae el historial y
 * descarta repeticiones). `por_mes` puede traer meses fuera del rango
 * pedido (AD8) — por eso itera `Object.keys`, nunca deriva el mes del
 * rango.
 *
 * F5 (review-13e17d2352297ac9): deliberadamente NO se filtra a "el mes
 * calendario en curso" (p.ej. `mes === mesActualToFecha(new Date())`) — eso
 * dependería del reloj (`Date.now()`), lo que volvería esta función pura de
 * render no-determinística y frágil de testear (un test que pasa hoy y
 * falla el primer día del mes que viene). Mostrar TODOS los meses que
 * `por_mes` trae, sin filtrar por "ahora", es la elección intencional que
 * evita reintroducir exactamente ese problema — no "arreglar" esto para que
 * escanee sólo el mes actual.
 *
 * Unit A2 (AD13, design.md): las filas con `estado === 'anulada'` se
 * IGNORAN acá — este panel dice reportar lo que el SERVIDOR contó, y el
 * servidor (`api/liquidacion.js` fase 1c/1d, AD7/AD8) ya no cuenta una
 * liquidación anulada para el tope. Mostrarla igual sería un número
 * confiadamente incorrecto. El historial de abajo (`renderHistorialPanel`)
 * oculta las filas `anulada` DETRÁS de un toggle "Mostrar anuladas (N)"
 * (owner UX batch, 2026-09-02) — nunca las borra ni deja de cargarlas, AD13
 * sigue intacta: siguen siendo alcanzables con un click, solo que ya no son
 * la vista por defecto (ver el propio docstring de `renderHistorialPanel`
 * para el detalle completo del toggle).
 * @param {Array<object>} rows filas de `listLiquidaciones`
 * @returns {string}
 */
function renderMesConsumoPanel(rows) {
    const vistos = new Set();
    const filas = [];
    for (const r of rows ?? []) {
        if (r.estado === 'anulada') continue;
        const porMes = r.detalle?.descuento_consumos?.por_mes;
        if (!porMes) continue;
        const nombre = r.empleados?.nombre ?? 'Empleado';
        for (const [mes, m] of Object.entries(porMes)) {
            const key = `${r.empleado_id}|${mes}`;
            if (vistos.has(key)) continue;
            vistos.add(key);
            filas.push({
                nombre,
                mes,
                consumido: (m.descuento_previo ?? 0) + (m.descuento_imputado ?? 0),
                tope: r.detalle.descuento_consumos.tope_mensual,
            });
        }
    }
    if (!filas.length) {
        return '<p class="text-xs text-text-muted mt-2">Sin consumo de tope registrado todavía.</p>';
    }
    return `<ul class="text-xs text-text-muted space-y-1 mt-2">${filas
        .map((f) => `<li>${escapeHTML(f.nombre)} — ${escapeHTML(f.mes)}: ${formatPrice(f.consumido)} de ${formatPrice(f.tope)}</li>`)
        .join('')}</ul>`;
}

/**
 * F3 (review-13e17d2352297ac9): `LiquidacionView` compone el resultado del
 * confirm, el preview y cada panel informativo en UN solo template
 * literal — sin esto, una excepción DENTRO de un panel informativo tira
 * abajo la expresión entera antes de que `js/admin.js` llegue a asignar
 * nada al mount, así que el confirm/preview ya calculados tampoco pintan.
 * Envuelve el render de un panel informativo en su propio try/catch: una
 * excepción ahí degrada SOLO ese panel a un mensaje corto, nunca al resto
 * de la vista — esto es lo que hace REAL el aislamiento que el comentario
 * de `renderHistorialPanel` (abajo) documenta, no sólo lo declara.
 *
 * F1 (loose end, review WARNING): la degradación es intencional (AD9), pero
 * antes esta función descartaba el error con un `catch` sin binding — cero
 * señal para nadie, indefinidamente. Reporta el error por el mismo canal
 * que ya usa el resto del código (`window.Sentry?.captureException` +
 * `console.error`, ver p.ej. `js/admin.js#refreshLiquidacionHistorial`),
 * antes de devolver el fallback — nunca lo deja en silencio.
 * @param {() => string} renderFn
 * @param {string} fallbackMensaje
 * @returns {string}
 */
function renderInformativePanel(renderFn, fallbackMensaje) {
    try {
        return renderFn();
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('renderInformativePanel: un panel informativo no pudo renderizar', err);
        return `<p role="status" class="text-xs text-text-muted mt-2">${escapeHTML(fallbackMensaje)}</p>`;
    }
}

const MEDIO_PAGO_LABELS = { efectivo: 'Efectivo', transferencia: 'Transferencia' };

/** `YYYY-MM-DD` (columna `date`) -> `DD/MM/YYYY`, sin pasar por `Date` (ni zona horaria). */
function formatFechaPago(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [anio, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}/${anio}`;
}

/**
 * L5 — pagos de una liquidación ANULADA, en la fila del historial. Una
 * anulación no anula sus pagos: sin esta lista quedaban invisibles. Sólo
 * lectura (fecha, medio, monto, anulado), más "Anular" en cada pago activo
 * (`anular_pago` lo permite con la liquidación anulada, migración 29), que
 * abre el cajón de pagos en modo sólo lectura. Sin pagos (o con la lectura
 * de pagos degradada, `null`) no se muestra nada.
 * @param {string} liquidacionId
 * @param {Array<object>|undefined} pagos
 * @returns {string}
 */
function renderPagosDeAnulada(liquidacionId, pagos) {
    if (!pagos?.length) return '';
    const items = pagos
        .map((p) => {
            const texto = `${formatFechaPago(p.fecha)} · ${escapeHTML(MEDIO_PAGO_LABELS[p.medio] ?? p.medio ?? '')} · ${formatMonto(p.monto)}`;
            const accion = p.anulado_at
                ? ' <span class="text-[10px] font-black uppercase tracking-widest">(anulado)</span>'
                : ` <button type="button" data-action="liquidacion-historial-pago-anular" data-id="${escapeHTML(liquidacionId)}" data-pago-id="${escapeHTML(p.id ?? '')}" class="ml-1 text-[10px] font-black uppercase tracking-widest text-red-700 underline">Anular</button>`;
            return `<li data-historial-pago data-id="${escapeHTML(p.id ?? '')}"${p.anulado_at ? ' class="line-through"' : ''}>${texto}${accion}</li>`;
        })
        .join('');
    return `
        <span class="block text-[11px] mt-1">Pagos de esta liquidación:</span>
        <ul class="text-[11px] pl-3 space-y-0.5">${items}</ul>
    `;
}

/**
 * Unit B2 (design.md AD12/AD13, spec "Anulación Is Visible and Explained in
 * the Admin Panel") — una fila individual del historial. `estado === 'anulada'`
 * es la ÚNICA condición que decide: nunca ofrece la acción, siempre muestra
 * la marca + motivo (escapado) + fecha; cualquier otro valor (incluida la
 * ausencia del campo, p.ej. un fixture legacy anterior a este change) se
 * trata como confirmada y SÍ ofrece la acción — `anulada` es terminal
 * (design.md Q5) y es el único estado que la CHECK de migración 27 admite
 * además de `confirmada`, así que no hace falta comparar contra
 * `'confirmada'` explícitamente acá.
 *
 * S8 (spec `payroll-payments` "Derived Payment State", "Register Payment
 * Drawer") — una fila `confirmada` ahora ADEMÁS muestra la pill de estado de
 * pago (`estadoPago`, mismo cálculo derivado que usa la pill "Liquidada" del
 * tablero — nunca una columna guardada) y "Registrar pago" al lado de
 * "Anular"/"Comprobante". Una fila `anulada` NO ofrece "Registrar pago" —
 * mismo criterio asimétrico que ya tiene "Anular": `registrar_pago` rechaza
 * `liquidacion_anulada` para un pago NUEVO (AD13), así que ofrecer el botón
 * ahí sería un camino que el servidor siempre va a rechazar.
 *
 * R1: una fila cuyos pagos NO se pudieron leer (`pagosDesconocidos`, una
 * página de "Ver más" cuya lectura de pagos falló o no respondió) nunca
 * muestra la pill — "Pendiente" sobre una liquidación quizás ya pagada
 * invita a pagarla dos veces — ni la lista de pagos de una anulada: dice
 * "No pudimos cargar los pagos.".
 * @param {object} r fila de `listLiquidaciones`
 * @param {Map<string, Array<object>>|null} pagosPorLiquidacion `liquidacionHistorialPagos` (`js/admin.js`) — `null` = la lectura de pagos del historial degradó, la pill se omite sin tirar abajo la fila
 * @param {Set<string>|null} [pagosDesconocidos] R1 — ids cuyos pagos no se pudieron leer (`liquidacionHistorialPagosDesconocidos`)
 * @returns {string}
 */
function renderHistorialRow(r, pagosPorLiquidacion, pagosDesconocidos = null) {
    const nombre = escapeHTML(r.empleados?.nombre ?? 'Empleado');
    const periodo = `${escapeHTML(r.periodo_desde ?? '')} a ${escapeHTML(r.periodo_hasta ?? '')}`;
    const base = `${nombre} — ${periodo} — Neto ${formatPrice(r.neto)}`;

    // Unit A2 (design.md AD5, spec "Comprobante Availability") — el
    // comprobante está disponible en TODA fila, `confirmada` O `anulada`; a
    // diferencia de "Anular", que sólo tiene sentido en una fila que todavía
    // no fue anulada.
    const comprobanteBtn = `<button type="button" data-action="liquidacion-comprobante" data-id="${escapeHTML(r.id ?? '')}" class="ml-2 text-[10px] font-black uppercase tracking-widest underline">Comprobante</button>`;
    const pagosDesconocidosHtml = pagosDesconocidos?.has(r.id)
        ? '<span class="block text-[11px] mt-0.5" data-pagos-desconocidos>No pudimos cargar los pagos.</span>'
        : null;

    if (r.estado === 'anulada') {
        return `
            <li class="text-text-muted/70">
                ${base}
                <span class="inline-block ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-black uppercase tracking-widest align-middle">Anulada</span>
                <span class="block text-[11px] mt-0.5">Anulada el ${escapeHTML(formatFechaHora(r.anulada_at))} — motivo: ${escapeHTML(r.anulada_motivo ?? '')}</span>
                ${comprobanteBtn}
                ${pagosDesconocidosHtml ?? renderPagosDeAnulada(r.id ?? '', pagosPorLiquidacion?.get(r.id))}
            </li>
        `;
    }

    // S8 — pill de estado de pago + "Registrar pago", SOLO en filas
    // `confirmada` (ver docstring de arriba). `pagosPorLiquidacion === null`
    // = la lectura de pagos del historial degradó (AD9): la fila sigue
    // mostrando todo lo demás, sólo la pill se omite. R1: pagos desconocidos
    // de ESTA fila -> el aviso en lugar de la pill.
    const pillHtml = pagosDesconocidosHtml
        ? pagosDesconocidosHtml
        : pagosPorLiquidacion
        ? (() => {
              const pill = estadoPago({ neto: r.neto, pagos: pagosPorLiquidacion.get(r.id) ?? [] }).pill;
              const clase = PAGO_PILL[pill.tono] ?? PAGO_PILL.pendiente;
              return `<span class="${clase}" data-pago-pill>${escapeHTML(pill.texto)}</span>`;
          })()
        : '';
    const pagoBtn = `<button type="button" data-action="liquidacion-historial-pago" data-id="${escapeHTML(r.id ?? '')}" class="ml-2 text-[10px] font-black uppercase tracking-widest underline">Registrar pago</button>`;

    return `
        <li>
            ${base}
            ${pillHtml}
            <button type="button" data-action="liquidacion-anular" data-id="${escapeHTML(r.id ?? '')}" class="ml-2 text-[10px] font-black uppercase tracking-widest text-red-700 underline">Anular</button>
            ${comprobanteBtn}
            ${pagoBtn}
        </li>
    `;
}

/**
 * L8 — "Ver más" del historial. Se ofrece mientras haya filas cargadas sin
 * mostrar (`visiblesCount > filasMostradas`) o todavía puedan existir
 * liquidaciones más viejas que la ventana (`verMas.hayMas`).
 * @param {number} visiblesCount
 * @param {number} filasMostradas
 * @param {{ hayMas?: boolean, cargando?: boolean, error?: string|null }|null} verMas
 * @returns {string}
 */
function renderVerMas(visiblesCount, filasMostradas, verMas) {
    const errorHtml = verMas?.error ? `<p role="status" class="text-xs text-text-muted mt-2">${escapeHTML(verMas.error)}</p>` : '';
    if (visiblesCount <= filasMostradas && !verMas?.hayMas) return errorHtml;
    const cargando = Boolean(verMas?.cargando);
    return `
        ${errorHtml}
        <button type="button" data-action="liquidacion-historial-ver-mas" ${cargando ? 'disabled' : ''} class="mt-3 text-[10px] font-black uppercase tracking-widest underline disabled:opacity-50">${cargando ? 'Cargando...' : 'Ver más'}</button>
    `;
}

/**
 * E2 — historial de liquidaciones confirmadas
 * (`js/infrastructure/liquidaciones.repository.js:listLiquidaciones`,
 * finalmente consumido). AD9: es de PANTALLA (browser, anon key) y NUNCA es
 * autoritativo para el cálculo de sueldos — eso vive server-side en
 * `api/liquidacion.js`. Por eso una falla acá degrada SOLO este bloque
 * (mensaje informativo) y NUNCA toca `preview`/`confirmResult` — se marca
 * "(informativo)" en el propio HTML para que quede explícito ante quien lo
 * mire, no sólo en un comentario. F3: esta garantía la hace cumplir
 * estructuralmente el caller (`renderInformativePanel`, abajo en
 * `LiquidacionView`) con un try/catch real — no sólo el orden en que se
 * llaman las funciones.
 *
 * Unit B2 (AD13): cada fila ofrece la acción de anular (`confirmada`) o
 * muestra la marca de anulada + motivo + fecha (`anulada`) vía
 * `renderHistorialRow`.
 *
 * Owner UX batch (2026-09-02): las filas `anulada` ahora se OCULTAN por
 * defecto (`mostrarAnuladas` falsy) detrás de un toggle "Mostrar anuladas
 * (N)" — AD13 seguía siendo literal ("nunca oculta") antes de este batch,
 * pero el dueño pidió reducir el ruido visual de un historial dominado por
 * filas anuladas viejas. La garantía DE FONDO de AD13 (el rastro de
 * auditoría nunca se DESTRUYE) se mantiene: ninguna fila se borra ni deja de
 * cargarse, sólo se filtra en el RENDER, y el toggle la trae de vuelta con
 * un click — spec "Anulación Is Visible and Explained in the Admin Panel"
 * sigue satisfecha porque la fila sigue siendo alcanzable, sólo que ya no es
 * la vista por defecto. El panel de consumo del tope (`renderMesConsumoPanel`,
 * ahora en el área de Configuración) NO se ve afectado por este toggle — ya
 * excluye las filas anuladas SIEMPRE, por su propia regla AD13/AD7/AD8,
 * independiente de si el historial las está mostrando o no.
 * S8: el toolbar "Exportar mes" (Unit B2 original) se RETIRÓ de acá —
 * mudado a "Exportar CSV" en la barra en lote del tablero
 * (`LiquidacionTableroView.js`, ver apply-progress.md "Unit S8"), el
 * comportamiento/policy de exportación no cambia.
 *
 * L8: muestra hasta `filasMostradas` filas visibles y "Ver más"
 * (`renderVerMas`) — también con la ventana vacía (R4), para que las
 * liquidaciones más viejas sigan alcanzables.
 * @param {{ cargando?: boolean, error?: string|null, liquidaciones?: Array<object> }|null} historial
 * @param {{
 *   mostrarAnuladas?: boolean,
 *   pagosPorLiquidacion?: Map<string, Array<object>>|null,
 *   pagosDesconocidos?: Set<string>|null,
 *   filasMostradas?: number,
 *   verMas?: { hayMas?: boolean, cargando?: boolean, error?: string|null }|null,
 * }} [opciones]
 *   - `mostrarAnuladas`: estado del toggle (`js/admin.js`, `liquidacionMostrarAnuladas`), `false` por defecto.
 *   - `pagosPorLiquidacion`: S8 — `liquidacionHistorialPagos`, ver `renderHistorialRow`.
 *   - `pagosDesconocidos`: R1 — ids cuyos pagos no se pudieron leer, ver `renderHistorialRow`.
 *   - `filasMostradas`: L8 — tope de filas visibles; el dueño del tamaño de página es `js/admin.js` (`LIQUIDACION_HISTORIAL_PAGINA`). Sin él se muestran todas las filas cargadas.
 *   - `verMas`: L8 — estado de la paginación de las más viejas (`liquidacionHistorialViejas`).
 * @returns {string}
 */
function renderHistorialPanel(historial, { mostrarAnuladas = false, pagosPorLiquidacion = null, pagosDesconocidos = null, filasMostradas, verMas = null } = {}) {
    const titulo = `<h3 class="${LABEL} mb-3">Historial de liquidaciones <span class="normal-case font-normal">(informativo)</span></h3>`;
    const wrap = (body) => `<div class="bg-surface border border-border-subtle rounded-2xl p-6 mb-8">${titulo}${body}</div>`;

    if (!historial || historial.cargando) {
        return wrap('<p class="text-sm text-text-muted">Cargando historial...</p>');
    }
    if (historial.error) {
        return wrap(`
            <p role="status" class="text-sm text-text-muted mb-4">${escapeHTML(historial.error)}</p>
            <button type="button" data-action="liquidacion-historial-retry" class="px-5 py-2 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-primary">Reintentar</button>
        `);
    }
    const rows = historial.liquidaciones ?? [];
    const cap = Number.isInteger(filasMostradas) && filasMostradas > 0 ? filasMostradas : rows.length;
    if (!rows.length) {
        // R4: la ventana vacía no significa que no haya liquidaciones más viejas.
        return wrap(`<p class="text-sm text-text-muted">Todavía no hay liquidaciones confirmadas.</p>${renderVerMas(0, cap, verMas)}`);
    }

    const anuladasCount = rows.filter((r) => r.estado === 'anulada').length;
    const toggleHtml = anuladasCount
        ? `
            <label class="flex items-center gap-2 mb-3 text-xs text-text-muted">
                <input type="checkbox" data-action="liquidacion-historial-mostrar-anuladas" ${mostrarAnuladas ? 'checked' : ''} class="w-4 h-4 rounded border-border-subtle text-brand focus:ring-brand" />
                Mostrar anuladas (${anuladasCount})
            </label>
        `
        : '';

    const visibles = mostrarAnuladas ? rows : rows.filter((r) => r.estado !== 'anulada');
    if (!visibles.length) {
        return wrap(`${toggleHtml}<p class="text-sm text-text-muted">No hay liquidaciones confirmadas para mostrar.</p>${renderVerMas(0, cap, verMas)}`);
    }
    const listaHtml = visibles
        .slice(0, cap)
        .map((r) => renderHistorialRow(r, pagosPorLiquidacion, pagosDesconocidos))
        .join('');
    return wrap(`
        ${toggleHtml}
        <ul class="text-xs text-text-muted space-y-1">${listaHtml}</ul>
        ${renderVerMas(visibles.length, cap, verMas)}
    `);
}

/**
 * Owner UX batch (2026-09-02): the monthly-cap editor (`renderCapEditor`)
 * and the month-consumed panel (`renderMesConsumoPanel`) moved out of the
 * main flow into a collapsible "Configuración" area (gear button in the
 * section header, `LiquidacionView`) — the editor is used rarely (setting
 * the cap once, occasionally adjusting it) but was permanently taking up a
 * large chunk of screen above the form the admin actually uses every day
 * (the range/employee selector). Each panel keeps its OWN
 * `renderInformativePanel` isolation (same as before this batch): a throw in
 * the cap editor degrades only the cap editor, a throw in the month-consumed
 * panel degrades only that panel — neither takes down the other or the rest
 * of the section, same AD9/F3 guarantee this file already documents at
 * `renderHistorialPanel`/`LiquidacionView`.
 * @param {object|null} capConfig
 * @param {Array<object>} historialRows filas de `listLiquidaciones` (sin filtrar por el toggle de anuladas — este panel excluye anuladas por su cuenta, AD13)
 * @returns {string}
 */
function renderConfigArea(capConfig, historialRows) {
    return `
        <div class="mb-2">
            ${renderInformativePanel(
                () => renderCapEditor(capConfig),
                'No pudimos mostrar el editor de tope mensual. El resto de la sección sigue funcionando con normalidad — una liquidación con sus cifras ya calculadas todavía se puede confirmar.',
            )}
            ${renderInformativePanel(
                () => `
                    <div class="bg-surface border border-border-subtle rounded-2xl p-6 mb-8">
                        <h4 class="${LABEL} mb-1">Consumo del tope por mes</h4>
                        ${renderMesConsumoPanel(historialRows)}
                    </div>
                `,
                'No pudimos mostrar el consumo del tope por mes.',
            )}
        </div>
    `;
}

function renderErrorGenerico(mensaje, retryAction) {
    return `
        <div role="alert" class="${BLOCKING_BOX}">
            <p class="font-bold mb-4">${escapeHTML(mensaje)}</p>
            <button data-action="${retryAction}" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
        </div>
    `;
}

/** Requisito 5: un rechazo parcial de confirm lista lo escrito y lo no-escrito, EXPLÍCITO. */
function renderRechazoParcial(confirmResult, empleados) {
    const nombrePorId = new Map((empleados ?? []).map((e) => [e.id, e.nombre]));
    const persistidas = confirmResult.persistidas ?? [];
    const nombreFallido = nombrePorId.get(confirmResult.empleadoId) ?? 'ese empleado';
    // periodo_duplicado significa que ESE empleado YA tiene una fila
    // (de este intento o de uno anterior) — nunca "no se guardó nada".
    const esDuplicado = confirmResult.errorCode === 'periodo_duplicado';

    const listaOk = persistidas.length
        ? `<ul class="list-disc pl-5 text-sm space-y-1 my-2">${persistidas
              .map((p) => `<li>${escapeHTML(nombrePorId.get(p.empleado_id) ?? 'Empleado')} — liquidación #${escapeHTML(p.liquidacion_id ?? '')} (${formatPrice(p.neto)})</li>`)
              .join('')}</ul>`
        : esDuplicado
          ? '<p class="text-sm my-2">Ninguna en ESTE intento — pero el empleado de abajo ya tenía una liquidación de antes.</p>'
          : '<p class="text-sm my-2">Ninguna — no se guardó nada todavía.</p>';

    const lineaFallido = esDuplicado
        ? `<p class="text-sm"><strong>${escapeHTML(nombreFallido)}</strong> YA ESTABA LIQUIDADO: ${escapeHTML(confirmResult.mensaje)} No es un fallo — no hace falta reintentarlo.</p>`
        : `<p class="text-sm">NO SE PUDO registrar la de <strong>${escapeHTML(nombreFallido)}</strong>: ${escapeHTML(confirmResult.mensaje)}. Si hace falta reintentar, hacelo SOLO para ese empleado.</p>`;

    const noIntentados = confirmResult.noIntentados ?? [];
    const noIntentadosHtml = noIntentados.length
        ? `<p class="text-sm mt-2">Sin intentar todavía (nunca se llegó a estos): ${noIntentados.map((id) => escapeHTML(nombrePorId.get(id) ?? 'Empleado')).join(', ')}.</p>`
        : '';

    return `
        <div role="alert" class="${BLOCKING_BOX}">
            <p class="font-bold mb-2">La liquidación quedó a mitad de camino.</p>
            <p class="text-sm">YA SE REGISTRARON estas liquidaciones en ESTE intento (no las vuelvas a confirmar):</p>
            ${listaOk}
            ${lineaFallido}
            ${noIntentadosHtml}
        </div>
    `;
}

/**
 * S8 fix (coordinator instruction, closes the risk flagged in
 * apply-progress.md "Unit S8" — R2's `kind: 'anomalias'` discovered at
 * CONFIRM time, not preview): mismo panel de resultado de confirm que
 * `rechazo_parcial`/`incierto`, nombrando a los empleados afectados —
 * NUNCA el error genérico, y NUNCA la lista cruda de fichadas por tipo (esa
 * era `renderAnomaliasGate`, retirada con `renderPreviewSection` — acá no
 * hay preview al que volver, sólo el confirm que hay que reintentar).
 * @param {{ nombres?: string[] }} confirmResult
 * @returns {string}
 */
function renderAnomaliasConfirmResult(confirmResult) {
    const nombres = confirmResult.nombres ?? [];
    const lista = nombres.length ? `: ${nombres.map((n) => escapeHTML(n)).join(', ')}` : '';
    return `
        <div role="alert" class="${WARNING_BOX}">
            <p class="font-bold mb-1">No se puede liquidar: hay fichadas pendientes de corrección${lista}.</p>
            <p class="text-sm">Corregilas en la sección Fichadas y volvé a intentar liquidar desde el tablero.</p>
        </div>
    `;
}

/** Requisito 5: transporte cortado en confirm — se muestra COMO INCIERTO, nunca como "error, probá de nuevo". */
function renderIncierto(mensaje) {
    return `
        <div role="alert" class="${WARNING_BOX}">
            <p class="font-bold mb-2">No sabemos si la liquidación se guardó.</p>
            <p class="text-sm">${escapeHTML(mensaje)}</p>
        </div>
    `;
}

/**
 * W1 — anulación `incierto` todavía no contrastada con el historial: aviso
 * fijo (no sólo el toast) hasta que el historial recarga bien.
 * @param {string|null|undefined} mensaje
 * @returns {string}
 */
function renderAnularIncierto(mensaje) {
    if (!mensaje) return '';
    return `
        <div role="alert" data-anular-incierto class="${WARNING_BOX}">
            <p class="font-bold mb-2">No sabemos si la anulación se aplicó.</p>
            <p class="text-sm">${escapeHTML(mensaje)}</p>
        </div>
    `;
}

function renderConfirmOk(liquidaciones, empleados) {
    const nombrePorId = new Map((empleados ?? []).map((e) => [e.id, e.nombre]));
    const items = (liquidaciones ?? [])
        .map((l) => `<li>${escapeHTML(nombrePorId.get(l.empleado_id) ?? 'Empleado')} — liquidación #${escapeHTML(l.liquidacion_id ?? '')} (${formatPrice(l.neto)})</li>`)
        .join('');
    return `
        <div role="status" class="bg-green-50 border border-green-300 text-green-900 rounded-2xl p-6 mb-6">
            <p class="font-bold mb-2">Liquidación confirmada.</p>
            <ul class="list-disc pl-5 text-sm space-y-1">${items}</ul>
        </div>
    `;
}

/**
 * @param {object|null} confirmResult
 * @param {Array<object>} empleados
 * @returns {string}
 */
function renderConfirmResultSection(confirmResult, empleados) {
    if (!confirmResult) return '';
    if (confirmResult.status === 'ok') return renderConfirmOk(confirmResult.liquidaciones, empleados);
    if (confirmResult.status === 'anomalias') return renderAnomaliasConfirmResult(confirmResult);
    if (confirmResult.status === 'parcial') return renderRechazoParcial(confirmResult, empleados);
    if (confirmResult.status === 'tope_no_configurado') return renderTopeNoConfigurado(confirmResult.mensaje);
    if (confirmResult.status === 'precondicion_bloqueante') return renderPrecondicionBloqueante(confirmResult, empleados);
    if (confirmResult.status === 'incierto') return renderIncierto(confirmResult.mensaje);
    if (confirmResult.status === 'error') return renderErrorGenerico(confirmResult.mensaje, 'liquidacion-confirm-result-dismiss');
    return '';
}

/**
 * @param {{ empleados: Array<object> }|null} viewModel usado solo en `status: 'ready'`.
 * @param {{ status: 'loading'|'error'|'ready', confirmResult?: object|null, anularIncierto?: string|null, capConfig?: object|null, historial?: object|null, historialPagos?: Map<string, Array<object>>|null, historialPagosDesconocidos?: Set<string>|null, historialFilasMostradas?: number, historialVerMas?: object|null, configAbierta?: boolean, historialMostrarAnuladas?: boolean }} [uiState]
 * @returns {string} HTML de la vista completa (header con botón de Configuración + resultado del confirm + historial, E2). S8: el selector mes/empleados y el preview por lote se retiraron — ver docstring de cabecera del archivo.
 */
export function LiquidacionView(viewModel, uiState = {}) {
    if (uiState.status === 'loading') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando...</p>
            </div>
        `;
    }

    if (uiState.status === 'error') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">No pudimos cargar la sección. Probá de nuevo.</p>
                <button data-action="liquidacion-retry" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>
        `;
    }

    const empleados = viewModel?.empleados ?? [];
    // Owner UX batch (2026-09-02): closed by default. The OPEN/CLOSED
    // decision itself — including "auto-open while the tope isn't
    // configured yet, so the admin sees what to do" — lives in `js/admin.js`
    // (`liquidacionConfigAbierta`, set by `refreshLiquidacionCapConfig`),
    // never inferred here: this pure render function only reflects whatever
    // boolean it is handed, the same way every other `uiState` flag in this
    // file already works.
    const configAbierta = Boolean(uiState.configAbierta);

    return `
        <section aria-label="Liquidación">
            <div class="flex items-center justify-between mb-5">
                <h2 class="text-lg font-black uppercase tracking-tight text-text-primary">Liquidación</h2>
                <button type="button" data-action="liquidacion-config-toggle" aria-label="Configuración de liquidación" aria-expanded="${configAbierta ? 'true' : 'false'}" class="flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-text-muted hover:text-text-primary focus:outline-none focus:border-brand">
                    <i class="fa-solid fa-gear" aria-hidden="true"></i>
                </button>
            </div>

            ${configAbierta ? renderConfigArea(uiState.capConfig, uiState.historial?.liquidaciones) : ''}
            ${renderConfirmResultSection(uiState.confirmResult, empleados)}
            ${renderAnularIncierto(uiState.anularIncierto)}
            ${renderInformativePanel(
                () =>
                    renderHistorialPanel(uiState.historial, {
                        mostrarAnuladas: Boolean(uiState.historialMostrarAnuladas),
                        pagosPorLiquidacion: uiState.historialPagos ?? null,
                        pagosDesconocidos: uiState.historialPagosDesconocidos ?? null,
                        filasMostradas: uiState.historialFilasMostradas,
                        verMas: uiState.historialVerMas ?? null,
                    }),
                'No pudimos mostrar el historial de liquidaciones. El resto de la sección sigue funcionando con normalidad.',
            )}
        </section>
    `;
}
